/**
 * QuickSearchDrawer - Inline results drawer for "rabbit hole" searches
 * 
 * Shows results in a slide-out drawer WITHOUT navigating away from the current page.
 * Uses fast cached lookups (no AI interpreter) for instant results.
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, Users, Loader2, ExternalLink, Search, ChevronRight, Sparkles, UserPlus, Mail, Briefcase, Building2, Globe, MapPin, DollarSign, Calendar, Phone } from 'lucide-react';
import api from '../../utils/axios';
import { useQuickSearch } from '../../contexts/QuickSearchContext';

// Helper to safely extract string from value that might be an object with {id, name}
const extractString = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value.name) return value.name;
  if (typeof value === 'object' && value.label) return value.label;
  return String(value);
};

// Mini lead card for quick results - click to view full profile
function QuickResultCard({ result, onClose: _onClose, onSelectLead, isSelected }) {
  const { triggerQuickSearch } = useQuickSearch();
  const data = result?.data || result;
  
  // Handle ZoomInfo's nested object format (e.g., company: {id, name})
  const firstName = extractString(data?.firstName) || '';
  const lastName = extractString(data?.lastName) || '';
  const fullName = extractString(data?.fullName);
  const name = fullName || (firstName && lastName ? `${firstName} ${lastName}` : extractString(data?.name)) || 'Unknown';
  
  const title = extractString(data?.jobTitle) || extractString(data?.title);
  const company = extractString(data?.companyName) || extractString(data?.company);
  const location = extractString(data?.city) || extractString(data?.location) || extractString(data?.state);
  const _linkedIn = extractString(data?.linkedInUrl) || extractString(data?.linkedin);
  
  // Get avatar/logo
  const getInitials = () => {
    if (name && name !== 'Unknown') {
      return name.split(' ').map(n => n[0]).filter(Boolean).join('').slice(0, 2).toUpperCase();
    }
    return '??';
  };

  const handleViewProfile = () => {
    if (onSelectLead) {
      onSelectLead(result);
    }
  };

  // Deep dive to find colleagues (secondary action)
  const _handleFindColleagues = (e) => {
    e.stopPropagation();
    if (company) {
      triggerQuickSearch('company', { name: company });
    }
  };
  
  return (
    <div 
      className={`p-3 border-b border-black/5 hover:bg-[var(--eq-purple)]/5 transition-colors cursor-pointer group ${isSelected ? 'bg-[var(--eq-purple)]/10 border-l-2 border-l-[var(--eq-purple)]' : ''}`}
      onClick={handleViewProfile}
    >
      <div className="flex items-center gap-3">
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--eq-purple)] to-[var(--eq-purple-dark)] flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
          {getInitials()}
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm text-black truncate group-hover:text-[var(--eq-purple)] transition-colors">{name || 'Unknown'}</div>
          {title && <div className="text-xs text-black/60 truncate">{title}</div>}
          {company && (
            <div 
              className="text-xs text-blue-600 font-medium truncate hover:underline cursor-pointer"
              onClick={(e) => { e.stopPropagation(); triggerQuickSearch('company', { name: company }); }}
            >
              {company}
            </div>
          )}
          {location && <div className="text-xs text-black/30 truncate">{location}</div>}
        </div>
        
        {/* View Profile indicator on hover */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="text-[10px] text-[var(--eq-purple)] font-medium">View</span>
          <ChevronRight className="w-4 h-4 text-[var(--eq-purple)]" />
        </div>
      </div>
    </div>
  );
}

// Full profile detail view
function ProfileDetailView({ lead, onBack, onFindColleagues: _onFindColleagues }) {
  const { triggerQuickSearch } = useQuickSearch();
  const data = lead?.data || lead;
  
  const firstName = extractString(data?.firstName) || '';
  const lastName = extractString(data?.lastName) || '';
  const fullName = extractString(data?.fullName);
  const name = fullName || (firstName && lastName ? `${firstName} ${lastName}` : extractString(data?.name)) || 'Unknown';
  
  const title = extractString(data?.jobTitle) || extractString(data?.title);
  const company = extractString(data?.companyName) || extractString(data?.company);
  const location = extractString(data?.city) || extractString(data?.location) || extractString(data?.state);
  const email = extractString(data?.email);
  const phone = extractString(data?.phone) || extractString(data?.directPhoneNumber);
  const linkedIn = extractString(data?.linkedInUrl) || extractString(data?.linkedin);
  const department = extractString(data?.department);
  const managementLevel = extractString(data?.managementLevel);
  
  const getInitials = () => {
    if (name && name !== 'Unknown') {
      return name.split(' ').map(n => n[0]).filter(Boolean).join('').slice(0, 2).toUpperCase();
    }
    return '??';
  };

  const handleFindColleagues = () => {
    if (company) {
      triggerQuickSearch('company', { name: company });
    }
  };

  const handleFindSimilar = () => {
    if (title) {
      triggerQuickSearch('job', { title });
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Back button */}
      <button 
        onClick={onBack}
        className="flex items-center gap-1 px-4 py-2 text-sm text-black/60 hover:text-black hover:bg-black/5 transition-colors"
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        Back to results
      </button>
      
      {/* Profile header */}
      <div className="p-4 border-b border-black/10">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--eq-purple)] to-[var(--eq-purple-dark)] flex items-center justify-center text-white text-xl font-semibold flex-shrink-0">
            {getInitials()}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-black">{name}</h2>
            {title && <p className="text-sm text-black/70">{title}</p>}
            {company && (
              <p 
                className="text-sm text-blue-600 font-medium hover:underline cursor-pointer"
                onClick={() => triggerQuickSearch('company', { name: company })}
              >
                {company}
              </p>
            )}
            {location && <p className="text-xs text-black/40 mt-1">{location}</p>}
          </div>
        </div>
      </div>
      
      {/* Contact details */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="space-y-3">
          <h3 className="text-xs font-semibold text-black/40 uppercase tracking-wide">Contact Information</h3>
          
          {email && (
            <a 
              href={`mailto:${email}`}
              className="flex items-center gap-3 p-3 bg-black/[0.02] rounded-lg hover:bg-black/5 transition-colors"
            >
              <Mail className="w-5 h-5 text-[var(--eq-purple)]" />
              <div>
                <div className="text-xs text-black/50">Email</div>
                <div className="text-sm text-black font-medium">{email}</div>
              </div>
            </a>
          )}
          
          {phone && (
            <a 
              href={`tel:${phone}`}
              className="flex items-center gap-3 p-3 bg-black/[0.02] rounded-lg hover:bg-black/5 transition-colors"
            >
              <UserPlus className="w-5 h-5 text-green-600" />
              <div>
                <div className="text-xs text-black/50">Phone</div>
                <div className="text-sm text-black font-medium">{phone}</div>
              </div>
            </a>
          )}
          
          {linkedIn && (
            <a 
              href={linkedIn}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-black/[0.02] rounded-lg hover:bg-black/5 transition-colors"
            >
              <ExternalLink className="w-5 h-5 text-blue-600" />
              <div>
                <div className="text-xs text-black/50">LinkedIn</div>
                <div className="text-sm text-black font-medium truncate">View Profile</div>
              </div>
            </a>
          )}
          
          {!email && !phone && !linkedIn && (
            <div className="text-sm text-black/40 italic p-3 bg-black/[0.02] rounded-lg">
              No contact details available. Use "Open as Agent" for enrichment.
            </div>
          )}
        </div>
        
        {/* Professional details */}
        {(department || managementLevel) && (
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-black/40 uppercase tracking-wide">Professional Details</h3>
            
            {department && (
              <div className="flex items-center gap-3 p-3 bg-black/[0.02] rounded-lg">
                <Briefcase className="w-5 h-5 text-black/30" />
                <div>
                  <div className="text-xs text-black/50">Department</div>
                  <div className="text-sm text-black font-medium">{department}</div>
                </div>
              </div>
            )}
            
            {managementLevel && (
              <div className="flex items-center gap-3 p-3 bg-black/[0.02] rounded-lg">
                <Users className="w-5 h-5 text-black/30" />
                <div>
                  <div className="text-xs text-black/50">Seniority</div>
                  <div className="text-sm text-black font-medium">{managementLevel}</div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Deep dive actions */}
        <div className="space-y-3">
          <h3 className="text-xs font-semibold text-black/40 uppercase tracking-wide">Deep Dive</h3>
          
          {company && (
            <button 
              onClick={handleFindColleagues}
              className="w-full flex items-center gap-3 p-3 bg-[var(--eq-purple)]/5 rounded-lg hover:bg-[var(--eq-purple)]/10 transition-colors text-left"
            >
              <Users className="w-5 h-5 text-[var(--eq-purple)]" />
              <div>
                <div className="text-sm text-[var(--eq-purple)] font-medium">Find Colleagues</div>
                <div className="text-xs text-black/50">People at {company}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-[var(--eq-purple)] ml-auto" />
            </button>
          )}
          
          {title && (
            <button 
              onClick={handleFindSimilar}
              className="w-full flex items-center gap-3 p-3 bg-blue-500/5 rounded-lg hover:bg-blue-500/10 transition-colors text-left"
            >
              <Briefcase className="w-5 h-5 text-blue-600" />
              <div>
                <div className="text-sm text-blue-600 font-medium">Find Similar Roles</div>
                <div className="text-xs text-black/50">Other {title}s</div>
              </div>
              <ChevronRight className="w-4 h-4 text-blue-600 ml-auto" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Company information display view
function CompanyInfoView({ company, onFindDecisionMakers, onFindPeople, onSelectAlternateCompany }) {
  if (!company) {
    return (
      <div className="flex flex-col items-center justify-center h-48 gap-3 p-4">
        <div className="p-3 bg-black/5 rounded-full">
          <Building2 className="w-6 h-6 text-black/30" />
        </div>
        <span className="text-sm text-black/50 text-center">Company not found in our database</span>
      </div>
    );
  }

  const formatEmployeeCount = (count) => {
    if (!count) return null;
    if (typeof count === 'number') {
      if (count >= 10000) return `${(count / 1000).toFixed(0)}K+`;
      if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
      return count.toString();
    }
    return count;
  };

  const formatRevenue = (revenue) => {
    if (!revenue) return null;
    if (typeof revenue === 'string') return revenue;
    if (typeof revenue === 'number') {
      if (revenue >= 1000000000) return `$${(revenue / 1000000000).toFixed(1)}B`;
      if (revenue >= 1000000) return `$${(revenue / 1000000).toFixed(0)}M`;
      return `$${revenue.toLocaleString()}`;
    }
    return revenue;
  };

  const location = [company.address?.city, company.address?.state, company.address?.country]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="flex flex-col h-full">
      {/* Company header */}
      <div className="p-4 border-b border-black/10 bg-gradient-to-br from-[var(--eq-purple)]/5 to-transparent">
        <div className="flex items-start gap-4">
          {/* Company logo or initial */}
          <div className="w-16 h-16 rounded-xl bg-white shadow-sm border border-black/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {company.logo ? (
              <img src={company.logo} alt={company.name} className="w-full h-full object-contain p-2" />
            ) : (
              <Building2 className="w-8 h-8 text-[var(--eq-purple)]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-black">{company.name}</h2>
            {company.industry && (
              <p className="text-sm text-[var(--eq-purple)] font-medium">{company.industry}</p>
            )}
            {location && (
              <p className="text-xs text-black/50 flex items-center gap-1 mt-1">
                <MapPin className="w-3 h-3" />
                {location}
              </p>
            )}
          </div>
        </div>
      </div>
      
      {/* Company details */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Quick stats */}
        {(company.employeeCount || company.revenue || company.founded || company.stockTicker) ? (
          <div className="grid grid-cols-2 gap-3">
            {company.employeeCount && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2 text-blue-600 mb-1">
                  <Users className="w-4 h-4" />
                  <span className="text-xs font-medium">Employees</span>
                </div>
                <div className="text-lg font-semibold text-black">
                  {formatEmployeeCount(company.employeeCount)}
                </div>
                {company.employeeRange && (
                  <div className="text-xs text-black/50">{company.employeeRange}</div>
                )}
              </div>
            )}
            
            {company.revenue && (
              <div className="p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2 text-green-600 mb-1">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-xs font-medium">Revenue</span>
                </div>
                <div className="text-lg font-semibold text-black">
                  {formatRevenue(company.revenue)}
                </div>
              </div>
            )}
            
            {company.founded && (
              <div className="p-3 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-2 text-purple-600 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span className="text-xs font-medium">Founded</span>
                </div>
                <div className="text-lg font-semibold text-black">{company.founded}</div>
              </div>
            )}
            
            {company.stockTicker && (
              <div className="p-3 bg-orange-50 rounded-lg">
                <div className="flex items-center gap-2 text-orange-600 mb-1">
                  <Briefcase className="w-4 h-4" />
                  <span className="text-xs font-medium">Stock</span>
                </div>
                <div className="text-lg font-semibold text-black">{company.stockTicker}</div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-4 bg-amber-50 rounded-lg text-sm text-amber-800">
            <p className="font-medium">Limited company data available</p>
            <p className="text-xs mt-1 text-amber-600">We have basic info for this company. Try searching for people to find more details.</p>
          </div>
        )}

        {/* Description */}
        {company.description && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-black/40 uppercase tracking-wide">About</h3>
            <p className="text-sm text-black/70 leading-relaxed">{company.description}</p>
          </div>
        )}
        
        {/* Links - only show if we have any */}
        {(company.website || company.linkedinUrl || company.phone) && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-black/40 uppercase tracking-wide">Links</h3>
            <div className="space-y-2">
              {company.website && (
                <a 
                  href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-black/[0.02] rounded-lg hover:bg-black/5 transition-colors"
                >
                  <Globe className="w-5 h-5 text-blue-600" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-black/50">Website</div>
                    <div className="text-sm text-blue-600 font-medium truncate">{company.website}</div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-black/30" />
                </a>
              )}
              
              {company.linkedinUrl && (
                <a 
                  href={company.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-black/[0.02] rounded-lg hover:bg-black/5 transition-colors"
                >
                  <ExternalLink className="w-5 h-5 text-blue-700" />
                  <div className="flex-1">
                    <div className="text-xs text-black/50">LinkedIn</div>
                    <div className="text-sm text-blue-700 font-medium">Company Page</div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-black/30" />
                </a>
              )}
              
              {company.phone && (
                <a 
                  href={`tel:${company.phone}`}
                  className="flex items-center gap-3 p-3 bg-black/[0.02] rounded-lg hover:bg-black/5 transition-colors"
                >
                  <Phone className="w-5 h-5 text-green-600" />
                  <div className="flex-1">
                    <div className="text-xs text-black/50">Phone</div>
                    <div className="text-sm text-black font-medium">{company.phone}</div>
                  </div>
                </a>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-black/40 uppercase tracking-wide">Find People</h3>
          <div className="space-y-2">
            <button 
              onClick={onFindDecisionMakers}
              className="w-full flex items-center gap-3 p-3 bg-[var(--eq-purple)]/5 rounded-lg hover:bg-[var(--eq-purple)]/10 transition-colors text-left"
            >
              <Sparkles className="w-5 h-5 text-[var(--eq-purple)]" />
              <div className="flex-1">
                <div className="text-sm text-[var(--eq-purple)] font-medium">Decision Makers</div>
                <div className="text-xs text-black/50">C-Level, VPs, Directors</div>
              </div>
              <ChevronRight className="w-4 h-4 text-[var(--eq-purple)]" />
            </button>
            
            <button 
              onClick={onFindPeople}
              className="w-full flex items-center gap-3 p-3 bg-blue-500/5 rounded-lg hover:bg-blue-500/10 transition-colors text-left"
            >
              <Users className="w-5 h-5 text-blue-600" />
              <div className="flex-1">
                <div className="text-sm text-blue-600 font-medium">All People</div>
                <div className="text-xs text-black/50">Browse employees</div>
              </div>
              <ChevronRight className="w-4 h-4 text-blue-600" />
            </button>
          </div>
        </div>

        {/* Alternate matches if any */}
        {company.alternateMatches && company.alternateMatches.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-black/40 uppercase tracking-wide">Similar Companies</h3>
            <div className="text-xs text-black/50">
              Did you mean one of these?
            </div>
            {company.alternateMatches.map((alt, idx) => (
              <button 
                key={idx} 
                onClick={() => onSelectAlternateCompany && onSelectAlternateCompany(alt)}
                className="w-full p-3 bg-black/[0.02] rounded-lg text-sm text-left hover:bg-black/5 transition-colors flex items-center justify-between group"
              >
                <div>
                  <span className="font-medium group-hover:text-[var(--eq-purple)]">{alt.name}</span>
                  {alt.industry && <span className="text-black/50"> • {alt.industry}</span>}
                </div>
                <ChevronRight className="w-4 h-4 text-black/30 group-hover:text-[var(--eq-purple)]" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function QuickSearchDrawer({ 
  isOpen, 
  onClose, 
  searchType, // 'people-at-company', 'similar-people', 'decision-makers', 'company-info'
  searchParams, // { companyName, jobTitle, etc. }
  title,
  onOpenAsAgent, // Optional: callback to open as full agent/search
}) {
  const { triggerQuickSearch } = useQuickSearch();
  const [results, setResults] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null); // For company-info type
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCached, setIsCached] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null); // For viewing full profile
  const drawerRef = useRef(null);
  
  const isCompanyInfoMode = searchType === 'company-info';
  
  // Reset state when drawer closes or searchParams change
  useEffect(() => {
    if (!isOpen) {
      setSelectedLead(null);
      setCompanyInfo(null);
    }
  }, [isOpen]);
  
  useEffect(() => {
    setSelectedLead(null);
    setCompanyInfo(null);
  }, [searchParams]);
  
  // Fetch results when opened
  useEffect(() => {
    if (!isOpen || !searchType || !searchParams) return;
    
    const fetchResults = async () => {
      setIsLoading(true);
      setError(null);
      setResults([]);
      setCompanyInfo(null);
      
      try {
        // For company-info, check if we have providedData to use directly
        if (searchType === 'company-info' && searchParams.providedData?.name) {
          const provided = searchParams.providedData;
          console.log('📋 Using provided company data:', provided.name);
          setCompanyInfo({
            name: provided.name,
            industry: provided.industry || null,
            employeeCount: provided.employeeCount || null,
            revenue: provided.revenue || null,
            website: provided.website || null,
            logo: provided.logo || null,
            description: provided.description || null,
            foundedYear: provided.foundedYear || null,
            linkedinUrl: provided.linkedinUrl || null,
            address: provided.location ? { city: provided.location } : null,
          });
          setIsCached(true); // Mark as cached since we're using provided data
          setIsLoading(false);
          return;
        }
        
        // Fallback to API call if no meaningful providedData
        const response = await api.post(`/api/quick-lookup/${searchType}`, searchParams);
        
        if (response.data?.success) {
          // Handle company-info differently
          if (searchType === 'company-info') {
            setCompanyInfo(response.data.result);
          } else {
            setResults(response.data.results || (response.data.result ? [response.data.result] : []));
          }
          setIsCached(response.data.cached || false);
        } else {
          setError(response.data?.error || response.data?.message || 'No results found');
        }
      } catch (err) {
        console.error('Quick lookup failed:', err);
        setError('Failed to fetch results');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchResults();
  }, [isOpen, searchType, searchParams]);
  
  // Helper to switch to decision makers view
  const handleFindDecisionMakers = () => {
    if (companyInfo?.name || searchParams?.companyName) {
      // Trigger a new search for decision makers
      triggerQuickSearch('decision-makers-direct', { 
        name: companyInfo?.name || searchParams?.companyName,
        companyId: companyInfo?.id 
      });
    }
  };
  
  // Helper to switch to all people view  
  const handleFindPeople = () => {
    if (companyInfo?.name || searchParams?.companyName) {
      triggerQuickSearch('people-at-company-direct', { 
        name: companyInfo?.name || searchParams?.companyName 
      });
    }
  };
  
  // Helper to select an alternate company match
  const handleSelectAlternateCompany = (altCompany) => {
    if (altCompany?.name) {
      triggerQuickSearch('company', { name: altCompany.name });
    }
  };
  
  // Close on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);
  
  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;
  
  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40 animate-in fade-in duration-150" />
      
      {/* Drawer */}
      <div 
        ref={drawerRef}
        className="fixed top-0 right-0 h-full w-full max-w-sm bg-white shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-black/10">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-[var(--eq-purple)]/10 rounded-lg">
              <Sparkles className="w-4 h-4 text-[var(--eq-purple)]" />
            </div>
            <div>
              <h3 className="font-semibold text-sm text-black">{title || 'Quick Lookup'}</h3>
              {isCached && (
                <span className="text-[10px] text-green-600 font-medium">⚡ Cached</span>
              )}
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-black/5 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-black/50" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Loading state */}
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <Loader2 className="w-8 h-8 text-[var(--eq-purple)] animate-spin" />
              <span className="text-sm text-black/50">Looking up...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3 p-4">
              <div className="p-3 bg-red-50 rounded-full">
                <X className="w-6 h-6 text-red-500" />
              </div>
              <span className="text-sm text-black/50 text-center">{error}</span>
            </div>
          ) : isCompanyInfoMode ? (
            /* Company information view */
            <CompanyInfoView 
              company={companyInfo}
              onFindDecisionMakers={handleFindDecisionMakers}
              onFindPeople={handleFindPeople}
              onSelectAlternateCompany={handleSelectAlternateCompany}
            />
          ) : selectedLead ? (
            /* Profile detail view if a lead is selected */
            <ProfileDetailView 
              lead={selectedLead} 
              onBack={() => setSelectedLead(null)}
            />
          ) : results.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3 p-4">
              <div className="p-3 bg-black/5 rounded-full">
                <Search className="w-6 h-6 text-black/30" />
              </div>
              <span className="text-sm text-black/50 text-center">No results found</span>
            </div>
          ) : (
            <div>
              {/* Results count */}
              <div className="px-4 py-2 bg-black/[0.02] border-b border-black/5">
                <span className="text-xs text-black/50">
                  {results.length} result{results.length !== 1 ? 's' : ''} • Click to view profile
                  {isCached && ' • ⚡ cached'}
                </span>
              </div>
              
              {/* Results list */}
              {results.map((result, idx) => (
                <QuickResultCard 
                  key={result?.id || idx} 
                  result={result} 
                  onClose={onClose}
                  isSelected={selectedLead?.id === result?.id}
                  onSelectLead={(lead) => {
                    console.log('Viewing profile:', lead);
                    setSelectedLead(lead);
                  }}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        {((results.length > 0 || (isCompanyInfoMode && companyInfo)) && onOpenAsAgent) && (
          <div className="p-4 border-t border-black/10">
            <button
              onClick={() => {
                onOpenAsAgent(searchParams);
                onClose();
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[var(--eq-purple)] text-white rounded-lg hover:bg-[var(--eq-purple-dark)] transition-colors text-sm font-medium"
            >
              <span>Open as Agent</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </>
  );
}

